// BILLING-PLANS (§6.1): the Plan area of Setup › Company › Billing — the Plan card, the agents
// and their tiers, the next invoice, invoices, and (phase 3) quotes and build projects. Owners,
// admins and an agency operator (reading) see it; only owners and admins pay, accept a quote or
// change the card (`can_manage`). Real rows only: a company the operator has not put on a plan
// says so.
import { EmptyState, InfoTip } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { LayersIcon } from "lucide-react";
import type { Api } from "../../../api/client";
import type { CompanyAccess } from "../../company";
import { LoadError, Loading } from "../../screen-parts";
import { ONE_TIME_CHARGES } from "../flags";
import { billingOverviewQuery, billingProjectsQuery, billingQuotesQuery } from "../plan-queries";
import { AgentsTable } from "./agents-table";
import { InvoicesTable } from "./invoices-table";
import { NextInvoice } from "./next-invoice";
import { PlanCard } from "./plan-card";
import { ProjectsCard } from "./projects-card";
import { QuotesTable } from "./quotes-table";

export function PlanArea({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const overview = useQuery(billingOverviewQuery(api, co));
  const quotes = useQuery({ ...billingQuotesQuery(api, co), enabled: ONE_TIME_CHARGES });
  const projects = useQuery({ ...billingProjectsQuery(api, co), enabled: ONE_TIME_CHARGES });
  if (overview.isPending) return <Loading label="Loading the plan" rows={4} />;
  if (overview.isError) return <LoadError what="The plan" error={overview.error} />;
  const o = overview.data;
  const sub = o.subscription;
  const currency = sub?.plan.currency ?? "USD";
  return (
    <section aria-label="Plan" data-slot="plan-area" className="flex min-w-0 flex-1 flex-col gap-6">
      {o.stripe_ready ? null : (
        <p
          className="flex items-center gap-1 text-[13px] text-muted-foreground"
          data-slot="stripe-off"
        >
          Payments are not connected yet.
          <InfoTip label="About payments">
            Stripe is not configured on this server, so nothing is charged and Pay links do not
            open.
          </InfoTip>
        </p>
      )}
      {o.pending_changes > 0 ? (
        <p
          className="flex items-center gap-1 text-[13px] text-muted-foreground"
          data-slot="billing-pending"
        >
          Updating
          <InfoTip label="About updating">
            A billing change is saved and on its way to the payment provider; it is retried
            automatically and shows here once confirmed.
          </InfoTip>
        </p>
      ) : null}
      {sub ? (
        <>
          <PlanCard api={api} companyId={co} overview={o} sub={sub} />
          <AgentsTable agents={o.agents} currency={currency} />
          {sub.upcoming ? <NextInvoice upcoming={sub.upcoming} /> : null}
        </>
      ) : (
        <EmptyState
          variant="card"
          icon={LayersIcon}
          title="No plan yet"
          description="Your account manager puts the company on a plan."
        />
      )}
      {ONE_TIME_CHARGES ? (
        <>
          <QuotesTable
            api={api}
            companyId={co}
            quotes={quotes.data?.data ?? []}
            canAccept={o.can_manage}
          />
          <ProjectsCard projects={projects.data?.data ?? []} />
        </>
      ) : null}
      <InvoicesTable api={api} companyId={co} canPay={o.can_manage} hideWhenEmpty={!sub} />
    </section>
  );
}
